/**
 * Form primitives shared by the egress connection editor and filter builder
 * (storyboard Flow W; ``shared/form-patterns.md``).
 *
 * Small, unopinionated wrappers over native controls. They exist so the two
 * shared patterns render identically and so every control carries a real
 * ``<label>`` association, a visible focus ring, and a hint slot without each
 * call site re-deriving the Tailwind string — the control classes are the
 * project's existing ones (``@/audit/audit-filter-bar``), not new tokens.
 *
 * **Native ``<select>`` / ``<input>`` on purpose.** A dense admin form is where
 * the platform controls are strongest: keyboard behaviour, type-ahead and
 * screen-reader semantics come for free, which is what REQ-UI-059 asks for, and
 * they add nothing to the air-gapped bundle.
 *
 * Cross-references:
 *   - REQ-UI-059 (landmarks + keyboard); REQ-UI-040 (both themes — colour comes
 *     only from tokens, so both palettes follow automatically).
 *   - docs/engineering/detail/storyboards/shared/form-patterns.md.
 */
import { useId, useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * The shared control treatment.
 *
 * Structure copied from ``@/audit/audit-filter-bar``, but the focus treatment
 * is the ``--brand`` one every other form in the app uses
 * (``@/profiles/profile-editor``, ``@/map/number-stepper``). ``--accent`` in
 * this codebase is shadcn's *interaction-state* slot — a near-background grey —
 * not the Appaloosa accent, so ``ring-accent`` renders an all-but-invisible
 * focus ring. ``--brand`` is the amber-on-dark / blue-on-light accent the
 * design-system amendment describes.
 */
export const CONTROL_CLASS =
  "rounded border border-border bg-bg-base px-2 py-1 text-body-sm text-text-primary " +
  "placeholder:text-text-muted focus:border-brand focus:outline-none " +
  "disabled:opacity-50";

/** Props for {@link PeerField}. */
export interface PeerFieldProps {
  label: string;
  /** One-line explanation rendered under the control. Operator-facing "why". */
  hint?: string;
  /** Validation message; rendered in the destructive token with ``role="alert"``. */
  error?: string | null;
  /** Receives the generated id so the control can be labelled. */
  children: (controlId: string) => ReactNode;
  className?: string;
}

/**
 * A labelled field row: ``<label for>`` + control + hint + error.
 *
 * The label is a real ``<label htmlFor>`` rather than an ``aria-label`` on the
 * control, so the text is a click target as well as an accessible name — in a
 * dense form the enlarged hit area is worth as much as the semantics.
 */
export function PeerField({
  label,
  hint,
  error,
  children,
  className,
}: PeerFieldProps): React.ReactElement {
  const id = useId();
  return (
    <div className={cn("flex flex-col gap-1", className)}>
      <label htmlFor={id} className="text-meta font-medium text-text-secondary">
        {label}
      </label>
      {children(id)}
      {hint && <p className="text-meta text-text-muted">{hint}</p>}
      {error && (
        <p role="alert" className="text-meta text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}

/** One option in an {@link PeerSelect}. */
export interface PeerSelectOption {
  value: string;
  label: string;
}

/** Props for {@link PeerSelect}. */
export interface PeerSelectProps {
  label: string;
  value: string;
  options: readonly PeerSelectOption[];
  onChange: (value: string) => void;
  hint?: string;
  disabled?: boolean;
  testId?: string;
}

/** A labelled native ``<select>``. */
export function PeerSelect({
  label,
  value,
  options,
  onChange,
  hint,
  disabled,
  testId,
}: PeerSelectProps): React.ReactElement {
  return (
    <PeerField label={label} hint={hint}>
      {(id) => (
        <select
          id={id}
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
          data-testid={testId}
          className={CONTROL_CLASS}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      )}
    </PeerField>
  );
}

/** Props for {@link PeerTextInput}. */
export interface PeerTextInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  hint?: string;
  error?: string | null;
  placeholder?: string;
  maxLength?: number;
  disabled?: boolean;
  /** ``true`` renders the value in the mono face (paths, ids, hostnames). */
  mono?: boolean;
  testId?: string;
}

/** A labelled single-line text input. */
export function PeerTextInput({
  label,
  value,
  onChange,
  hint,
  error,
  placeholder,
  maxLength,
  disabled,
  mono,
  testId,
}: PeerTextInputProps): React.ReactElement {
  return (
    <PeerField label={label} hint={hint} error={error}>
      {(id) => (
        <input
          id={id}
          type="text"
          value={value}
          placeholder={placeholder}
          maxLength={maxLength}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
          data-testid={testId}
          className={cn(CONTROL_CLASS, mono && "font-mono")}
        />
      )}
    </PeerField>
  );
}

/** Props for {@link PeerNumberInput}. */
export interface PeerNumberInputProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  /**
   * ``"any"`` for continuous quantities — timeouts, backoffs, confidence,
   * degrees. Omit (or pass an integer) for counts and ports.
   *
   * Load-bearing: HTML step validation anchors on ``min``, so a numeric ``step``
   * beside a fractional ``min`` silently makes most real values invalid (0.1
   * with step 0.5 admits 0.6 and 1.1, not 1) and the browser then **refuses to
   * submit the whole form** with no message the operator can act on. A
   * continuous field must therefore say so rather than pick a granularity.
   */
  step?: number | "any";
  hint?: string;
  error?: string | null;
  testId?: string;
}

/**
 * A labelled numeric input.
 *
 * A blank or non-numeric entry is **not** coerced to zero — it is passed
 * through as ``NaN`` for the caller's validator to reject. Coercing would turn
 * a half-typed port into port 0 and a cleared timeout into an instant one; on a
 * peer-manager path the operator must be told, not silently corrected.
 */
export function PeerNumberInput({
  label,
  value,
  onChange,
  min,
  max,
  step,
  hint,
  error,
  testId,
}: PeerNumberInputProps): React.ReactElement {
  return (
    <PeerField label={label} hint={hint} error={error}>
      {(id) => (
        <input
          id={id}
          type="number"
          value={Number.isNaN(value) ? "" : value}
          min={min}
          max={max}
          step={step}
          onChange={(event) => onChange(event.target.valueAsNumber)}
          data-testid={testId}
          className={cn(CONTROL_CLASS, "w-full font-mono")}
        />
      )}
    </PeerField>
  );
}

/** Props for {@link PeerCheckboxSet}. */
export interface PeerCheckboxSetProps {
  label: string;
  options: readonly string[];
  selected: readonly string[];
  onChange: (next: string[]) => void;
  hint?: string;
  /** Per-option display label; defaults to the raw value. */
  renderLabel?: (value: string) => string;
  testIdPrefix?: string;
}

/**
 * A checkbox group over a **closed** vocabulary.
 *
 * Used wherever the filter's allow-set is drawn from a fixed enum (message
 * types, BSI alert types, BSI priorities). Offering the list rather than a text
 * box removes the whole class of filters that silently match nothing because a
 * name was mistyped — filter matching is exact and un-normalized by design.
 */
export function PeerCheckboxSet({
  label,
  options,
  selected,
  onChange,
  hint,
  renderLabel,
  testIdPrefix,
}: PeerCheckboxSetProps): React.ReactElement {
  const toggle = (value: string): void => {
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
  };
  return (
    <fieldset className="flex flex-col gap-1">
      <legend className="text-meta font-medium text-text-secondary">{label}</legend>
      <div className="flex flex-wrap gap-x-3 gap-y-1">
        {options.map((option) => (
          <label
            key={option}
            className="flex cursor-pointer items-center gap-1.5 text-body-sm text-text-primary"
          >
            <input
              type="checkbox"
              checked={selected.includes(option)}
              onChange={() => toggle(option)}
              data-testid={testIdPrefix ? `${testIdPrefix}-${option}` : undefined}
              className="h-3.5 w-3.5 cursor-pointer"
            />
            <span className="font-mono">{renderLabel?.(option) ?? option}</span>
          </label>
        ))}
      </div>
      {hint && <p className="text-meta text-text-muted">{hint}</p>}
    </fieldset>
  );
}

/** Props for {@link PeerTokenList}. */
export interface PeerTokenListProps {
  label: string;
  values: readonly string[];
  onChange: (next: string[]) => void;
  hint?: string;
  placeholder?: string;
  testId?: string;
}

/**
 * A chip list for an **open** vocabulary — producer ids, aggregator ids, BSI
 * classification strings.
 *
 * Entries are added on Enter (or the Add button) and never normalized: filter
 * matching is exact, and case-folding an operator's entry would be a guess
 * about a deployment's sensor conventions that, on an egress boundary, could
 * emit data they did not intend. Duplicates collapse, because the underlying
 * document is a set.
 */
export function PeerTokenList({
  label,
  values,
  onChange,
  hint,
  placeholder,
  testId,
}: PeerTokenListProps): React.ReactElement {
  const [draft, setDraft] = useState("");
  const commit = (): void => {
    const token = draft.trim();
    if (token === "") return;
    if (!values.includes(token)) onChange([...values, token]);
    setDraft("");
  };
  return (
    <PeerField label={label} hint={hint}>
      {(id) => (
        <>
          <div className="flex gap-1">
            <input
              id={id}
              type="text"
              value={draft}
              placeholder={placeholder}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  commit();
                }
              }}
              data-testid={testId}
              className={cn(CONTROL_CLASS, "min-w-0 flex-1 font-mono")}
            />
            <button
              type="button"
              onClick={commit}
              className="rounded border border-border px-2 text-body-sm text-text-primary hover:bg-bg-elevated"
            >
              Add
            </button>
          </div>
          {values.length > 0 && (
            <ul className="flex flex-wrap gap-1 pt-1">
              {values.map((value) => (
                <li key={value}>
                  <span className="inline-flex items-center gap-1 rounded border border-border bg-bg-elevated px-1.5 py-0.5 font-mono text-meta text-text-primary">
                    {value}
                    <button
                      type="button"
                      aria-label={`Remove ${value}`}
                      onClick={() => onChange(values.filter((v) => v !== value))}
                      className="text-text-secondary hover:text-destructive"
                    >
                      ✕
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </PeerField>
  );
}
